import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { optionSetsKey as productOptionSetsKey } from '@/features/products'

import { createOptionSet, deleteOptionSet, fetchOptionSets, updateOptionSet } from '../api'
import type { SaveOptionSetBody } from '../types'

export const optionSetsKey = ['option-sets'] as const

export function useOptionSets() {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...optionSetsKey, 'list'],
    queryFn: fetchOptionSets,
  })

  return {
    optionSets: data ?? [],
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useOptionSetActions() {
  const queryClient = useQueryClient()

  // The product form reads the same sets through its own key, so an edit here has
  // to reach that cache too — otherwise the picker keeps offering yesterday's axes.
  function invalidate() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: optionSetsKey }),
      queryClient.invalidateQueries({ queryKey: productOptionSetsKey }),
    ])
  }

  /**
   * One call either way — unlike choice groups, the option-set update endpoint
   * takes the whole body, items included, and replaces them.
   */
  const save = useMutation({
    mutationFn: async ({ id, body }: { id: string | null; body: SaveOptionSetBody }) => {
      if (id) await updateOptionSet(id, body)
      else await createOptionSet(body)
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => deleteOptionSet(id),
    onSuccess: invalidate,
  })

  return { save, remove }
}
